import { ChatbotStore, ChatbotAuthToken } from "./ChatbotStore";
import { SerializedData, Store, AnalyticsStore } from "bernie-core";
import { Logger, NOOP_LOGGER, SystemEvent, SystemEventLevel } from "bernie-logger";
import { observable } from "mobx";

interface VacEvent {
  type: string;
}

export interface WebChatObject {
  initialize: (arg0: MessengerSettings) => Promise<any>;
  openChatWidget: () => void;
  setConversationContext: (arg0: object) => void;
  isInitialized: Promise<any>;
}

//currently a subset of: https://github.expedia.biz/ECP/gco-c3-web-messenger/blob/aac116d5960fc34ef365fa14ca908838860972ba/src/c3-web-chat.js
export interface WindowWithChatbot extends Window {
  c3WebChat?: WebChatObject;
  virtualAgentControl?: WebChatObject;
}

interface MessengerSettings {
  authToken: ChatbotAuthToken;
  languageId?: string;
  channelOriginId: string;
  subscribedEvents: string[];
  eventHandler: (event: VacEvent, analyticsStore: AnalyticsStore) => void;
}

export interface ConversationProperties {
  launchPoint: string;
  pageName: string;
}

export interface ConversationIntent {
  intent: string;
}

export enum IntentTypes {
  VIEW_BOOKING = "VIEW_BOOKING",
  REFUND_STATUS = "REFUND_STATUS",
}

export enum VacEventTypes {
  VAC_WIDGET_OPENED_EVENT = "VAC_WidgetOpened",
  VAC_WIDGET_CLOSED_EVENT = "VAC_WidgetClosed",
  VAC_WIDGET_READY_EVENT = "VAC_Ready",
  VAC_WIDGET_OPENED_RFRR_ID = "VA.FAB.Open",
  VAC_WIDGET_CLOSED_RFRR_ID = "VA.FAB.Closed",
  VAC_WIDGET_READY_RFRR_ID = "VA.FAB.Ready",
  VAC_WIDGET_OPENED_DESCRIPTION = "VAC Widget Opened",
  VAC_WIDGET_CLOSED_DESCRIPTION = "VAC Widget Closed",
  VAC_WIDGET_READY_DESCRIPTION = "VAC Widget Ready",
}

export enum VacLoggingTypes {
  VAC_BAD_RESPONSE_ERROR = "CHATBOT_INIT_ERROR_VAC_BAD_RESPONSE",
  VAC_INIT_ERROR = "CHATBOT_INIT_ERROR_CALLER_INCORRECT_CALL",
  VAC_SUCCESS = "CHATBOT_INITIALIZED",
}

export class ClientChatbotStore extends Store implements ChatbotStore {
  @observable
  public chatbotInitialized: boolean;

  public authToken: ChatbotAuthToken;
  public channelOriginId: string;
  public staticJSScript: string;
  public locale?: string;
  public chatbotExperimentOn?: boolean;
  public chatbotUseGcPath?: boolean;
  public chatbotUseVacPath?: boolean;
  public subscribedEvents = [
    VacEventTypes.VAC_WIDGET_OPENED_EVENT,
    VacEventTypes.VAC_WIDGET_CLOSED_EVENT,
    VacEventTypes.VAC_WIDGET_READY_EVENT,
  ];

  public constructor(state: SerializedData = {}, logger: Logger = NOOP_LOGGER) {
    super(state, logger);
    this.chatbotInitialized = false;
  }

  public hydrate(data: SerializedData): void {
    this.authToken = data.authToken;
    this.channelOriginId = data.channelOriginId;
    this.authToken = data.authToken;
    this.staticJSScript = data.staticJSScript;
    this.locale = data.locale;
    this.chatbotExperimentOn = data.chatbotExperimentOn;
    this.chatbotUseGcPath = data.chatbotUseGcPath;
    this.chatbotUseVacPath = data.chatbotUseVacPath;
  }

  public injectChatbotScriptAndInitializeOnLoad(document: Document, analytics: AnalyticsStore, initLogger: Logger) {
    let script;
    if (this.chatbotUseGcPath && this.locale && this.staticJSScript) {
      script = document.createElement("script");
      script.src = this.staticJSScript;
      script.onload = () =>
        this.tryInitializeChatbot(
          analytics,
          initLogger,
          this.getWebChatObject((window as unknown) as WindowWithChatbot)
        );
      document.body.appendChild(script);
    }
  }

  public getWebChatObject(window: WindowWithChatbot) {
    if (window.c3WebChat) {
      return window.c3WebChat;
    } else if (window.virtualAgentControl) {
      return window.virtualAgentControl;
    }

    return undefined;
  }

  public initializeChatbot(analytics: AnalyticsStore, initLogger: Logger, webChatObject?: WebChatObject) {
    const messengerSettings: MessengerSettings = {
      authToken: this.authToken,
      languageId: this.locale,
      channelOriginId: this.channelOriginId,
      subscribedEvents: this.subscribedEvents,
      eventHandler: (event) => this.eventHandler(analytics, event),
    };

    if (webChatObject && this.locale) {
      webChatObject
        .initialize(messengerSettings)
        .then(() => {
          this.chatbotInitialized = true;
          initLogger.logEvent(new SystemEvent(SystemEventLevel.INFO, VacLoggingTypes.VAC_SUCCESS));
        })
        .catch((err: any) => {
          initLogger.logEvent(new SystemEvent(SystemEventLevel.WARN, VacLoggingTypes.VAC_BAD_RESPONSE_ERROR), err);
        });
    }
  }

  public async tryInitializeChatbot(analytics: AnalyticsStore, initLogger: Logger, webChatObject?: WebChatObject) {
    try {
      this.initializeChatbot(analytics, initLogger, webChatObject);
    } catch (e) {
      initLogger.logEvent(new SystemEvent(SystemEventLevel.WARN, VacLoggingTypes.VAC_INIT_ERROR), e);
    }
  }

  public eventHandler(analyticsStore: AnalyticsStore, event: VacEvent) {
    switch (event.type) {
      case VacEventTypes.VAC_WIDGET_OPENED_EVENT:
        analyticsStore.trackEvent(
          VacEventTypes.VAC_WIDGET_OPENED_RFRR_ID,
          VacEventTypes.VAC_WIDGET_OPENED_DESCRIPTION,
          analyticsStore.omnitureData
        );
        break;
      case VacEventTypes.VAC_WIDGET_CLOSED_EVENT:
        analyticsStore.trackEvent(
          VacEventTypes.VAC_WIDGET_CLOSED_RFRR_ID,
          VacEventTypes.VAC_WIDGET_CLOSED_DESCRIPTION,
          analyticsStore.omnitureData
        );
        break;
      case VacEventTypes.VAC_WIDGET_READY_EVENT:
        analyticsStore.trackEvent(
          VacEventTypes.VAC_WIDGET_READY_RFRR_ID,
          VacEventTypes.VAC_WIDGET_READY_DESCRIPTION,
          analyticsStore.omnitureData
        );
        break;
      default:
        break;
    }
  }

  public async startConversation(
    conversationProperties: ConversationProperties,
    conversationIntent: ConversationIntent
  ) {
    const windowChatObject = this.getWebChatObject(window as WindowWithChatbot);
    const initialized = await windowChatObject?.isInitialized;
    if (initialized) {
      const conversationContext = {
        conversationProperties,
        intentMessage: conversationIntent,
      };
      windowChatObject?.setConversationContext(conversationContext);
    }
  }
}
